import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  type Placement,
} from '@floating-ui/react';
import clsx from 'clsx';
import {
  memo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

import { resolveTopLayerRoot } from '../../../../../utils/resolveTopLayerRoot';
import './NavMenu.css';

type NavMenuProps = {
  /**
   * Plain content, or a render function given `close` to call after an
   * action so the menu doesn't linger open once its job is done (e.g. after
   * navigating, or after triggering something like a confirmation modal or
   * log out that won't itself take focus away).
   */
  content: ReactNode | ((props: { close: () => void }) => ReactNode);
  trigger: ReactNode;
  /**
   * Where the flyout opens relative to the trigger.
   * @default 'right-start' — the SideNav compact-rail convention: always
   * flyout to the right of the collapsed rail, with a fixed fallback
   * placement and no vertical-axis flip (a rail item always has open canvas
   * to its right), opens on hover as well as click, and ArrowRight/ArrowLeft
   * open/close it. Passing any other placement switches to full flip + shift
   * on both axes instead, for triggers where "open to the right" may have
   * nowhere to go (like a corner-anchored top-bar avatar), and drops hover
   * and the rail's arrow keys — only a deliberate click opens it.
   */
  placement?: Placement;
};

type OpenReason = 'hover' | 'intent';

// A data attribute rather than `.eds-nav-link--button` so arrow-key nav
// still finds items when `content` isn't built from `NavLink` (e.g. the
// profile menu's own item markup) — anything meant to be arrow-key
// navigable opts in with `data-eds-nav-menu-item`, independent of its class.
const MENU_ITEM_SELECTOR = '[data-eds-nav-menu-item]';

const getMenuItems = (menu: HTMLElement) =>
  Array.from(menu.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR));

/**
 * Compact-mode side-nav flyout. Portals the menu so it stays visible outside
 * the nav's scroll container and does not expand the nav's scrollable overflow.
 * Opens on hover (mouse), click/tap/Enter/Space (useClick), or ArrowRight —
 * the rail's default placement only, see the `placement` prop.
 */
export const NavMenu = memo(({ content, trigger, placement }: NavMenuProps) => {
  const isRailFlyout = placement === undefined;
  const resolvedPlacement = placement ?? 'right-start';

  const [isOpen, setIsOpen] = useState(false);
  const [openReason, setOpenReason] = useState<OpenReason>('hover');
  const [portalRoot, setPortalRoot] = useState<HTMLElement | undefined>();
  const triggerRef = useRef<HTMLElement | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(4),
      // Rail flyouts only ever consider the right side (fallback stays
      // right-end, no vertical-axis flip); a caller-supplied placement gets
      // full flip so it can reposition wherever the trigger leaves no room.
      isRailFlyout
        ? flip({
            fallbackPlacements: ['right-end'],
            mainAxis: false,
          })
        : flip(),
      isRailFlyout
        ? shift({
            crossAxis: true,
            mainAxis: false,
            padding: 8,
          })
        : shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(80, availableHeight)}px`,
          });
        },
      }),
    ],
    open: isOpen,
    placement: resolvedPlacement,
    // A top-layer host (an open popover/dialog) clips the portalled panel
    // when it hides its own overflow; fixed positioning escapes that
    // without leaving the layer (paired with resolveTopLayerRoot below).
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    onOpenChange: (nextOpen, _event, reason) => {
      if (nextOpen) {
        setPortalRoot(resolveTopLayerRoot(triggerRef.current));
      }
      setIsOpen(nextOpen);
      if (!nextOpen) {
        setOpenReason('hover');
        return;
      }
      // Click covers pointer, touch, and native button Enter/Space.
      if (reason === 'click') {
        setOpenReason('intent');
      }
    },
  });

  // mouseOnly: touch/pen open via useClick so hover+click don't fight on
  // tap. Hover-to-open is the rail's scan-down-the-list convention — off for
  // a caller-supplied placement, where only a deliberate click should open
  // the menu (e.g. a top-bar avatar shouldn't pop its account menu on a
  // passing mouseover).
  const hover = useHover(context, {
    enabled: isRailFlyout,
    handleClose: safePolygon(),
    mouseOnly: true,
    move: false,
  });
  const click = useClick(context, { toggle: true });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
    role,
  ]);

  const triggerRefs = useMergeRefs([refs.setReference, triggerRef]);

  // ArrowRight is the submenu-open key for a right-side flyout — Floating UI
  // does not map that; Enter/Space go through useClick via the nested button.
  // Only meaningful for the rail's fixed rightward direction.
  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!isRailFlyout || event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    setOpenReason('intent');
    setIsOpen(true);
  };

  const handleMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (isRailFlyout && event.key === 'ArrowLeft') {
      event.preventDefault();
      setIsOpen(false);
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    const items = getMenuItems(event.currentTarget);
    if (items.length === 0) {
      return;
    }

    event.preventDefault();
    const currentIndex = items.findIndex(
      (item) => item === document.activeElement,
    );
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex =
      currentIndex === -1
        ? 0
        : (currentIndex + delta + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  return (
    <>
      <div
        className={clsx('eds-nav-menu--trigger', {
          'eds-nav-menu--trigger--rail': isRailFlyout,
        })}
        ref={triggerRefs}
        {...getReferenceProps({
          onKeyDown: handleTriggerKeyDown,
        })}
      >
        {trigger}
      </div>
      {isOpen ? (
        <FloatingPortal root={portalRoot}>
          <FloatingFocusManager
            context={context}
            initialFocus={openReason === 'intent' ? 0 : -1}
            modal={false}
            returnFocus
          >
            <div
              className={clsx('eds-nav-menu', {
                'eds-nav-menu--rail': isRailFlyout,
              })}
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps({
                onKeyDown: handleMenuKeyDown,
              })}
            >
              {typeof content === 'function'
                ? content({ close: () => setIsOpen(false) })
                : content}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  );
});

NavMenu.displayName = 'NavMenu';
